import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Form,
  InputGroup,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { MdEditSquare } from "react-icons/md";
import {
  FaChevronRight,
  FaPaypal,
  FaCcVisa,
  FaCcMastercard,
  FaCcDiscover,
  FaCcAmex,
  FaExclamationCircle,
  FaCalendarAlt,
  FaClock,
} from "react-icons/fa";
import { SiKlarna } from "react-icons/si";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import "./BookingForm.css";
import StepOne from "./StepOne";
import StepTwoWrapper from "./StepTwo";
import StepThree from "./StepThree";
import StepFour from "./StepFour";
import StepFive from "./StepFive";
import NavbarMenu from "../heroSection/NavbarMenu";
import Footer from "../footer/Footer";
import { userAPI } from "../../api/apiSystem";
import { formatDisplayDate } from "../../utils/helpers";

// ==================== Constants ====================
const EXTRA_SERVICE_PRICE = 25;
const VAT_RATE = 0.19;
const BASE_PRICE_PER_KG = 0.095;

const SHIPMENT_TYPE_MAP = {
  Palette: "pallete",
  Paket: "pakete",
  Dokument: "dokument",
  Fhrrader: "fahrrader",
  "Komplettes Fahrzeug": "komplettes fahrzeug",
  Sonstige: "other",
};

const REVERSE_SHIPMENT_TYPE_MAP = {
  pallete: "Palette",
  pakete: "Paket",
  dokument: "Dokument",
  fahrrader: "Fhrrader",
  "komplettes fahrzeug": "Komplettes Fahrzeug",
  other: "Sonstige",
};

// ==================== Helper Functions ====================
const createDefaultShipment = (type = "Palette") => {
  if (type === "Dokument") {
    return {
      quantity: 1,
      type: "Dokument",
      weight: 0,
      length: 0,
      width: 0,
      height: 0,
      notes: "",
    };
  }

  return {
    quantity: 1,
    type: type,
    weight: 100,
    length: 120,
    width: 80,
    height: 100,
    notes: "",
  };
};

const createDefaultPickup = () => ({
  companyName: "",
  firstName: "",
  lastName: "",
  street: "",
  addressExtra: "",
  postalCity: "",
  postalCity_lat: "",
  postalCity_lng: "",
  phone: "",
  pickupReference: "",
  pickupDate: "",
  timeFrom: "",
  timeTo: "",
});

const createDefaultDelivery = () => ({
  companyName: "",
  firstName: "",
  lastName: "",
  street: "",
  addressExtra: "",
  postalCity: "",
  postalCity_lat: "",
  postalCity_lng: "",
  phone: "",
  deliveryReference: "",
  deliveryDate: "",
  timeFrom: "",
  timeTo: "",
});

const parseAmountValue = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const normalized = String(value)
    .trim()
    .replace(/[^\d,.-]/g, "");

  if (!normalized) {
    return 0;
  }

  const lastComma = normalized.lastIndexOf(",");
  const lastDot = normalized.lastIndexOf(".");
  const hasComma = lastComma !== -1;
  const hasDot = lastDot !== -1;

  let numeric = normalized;

  if (hasComma && hasDot) {
    if (lastComma > lastDot) {
      numeric = numeric.replace(/\./g, "").replace(",", ".");
    } else {
      numeric = numeric.replace(/,/g, "");
    }
  } else if (hasComma) {
    numeric = numeric.replace(/\./g, "").replace(",", ".");
  } else if (hasDot) {
    numeric = numeric.replace(/,/g, "");
  }

  const parsed = Number(numeric);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatAmountValue = (value) => parseAmountValue(value).toFixed(2).replace(".", ",");

const applyCouponToPrice = (price, coupon) => {
  if (!coupon?.discountPercentage) {
    return price;
  }

  const subTotal = parseAmountValue(price.subTotal);
  const total = parseAmountValue(price.total);
  const multiplier = subTotal > 0 ? total / subTotal : 1 + VAT_RATE;
  const discountedSubTotal = subTotal - (subTotal * coupon.discountPercentage) / 100;
  const discountedTotal = discountedSubTotal * multiplier;

  return {
    subTotal: formatAmountValue(discountedSubTotal),
    total: formatAmountValue(discountedTotal),
  };
};

const convertShipmentType = (type) => {
  return REVERSE_SHIPMENT_TYPE_MAP[type?.toLowerCase()] || "Palette";
};

const getStoredUser = () => {
  if (typeof window === "undefined") return null;

  try {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  } catch {
    return null;
  }
};

const parseDurationMinutes = (durationText) => {
  if (!durationText) return 0;

  const text = String(durationText).toLowerCase();
  const hours = Number(text.match(/(\d+)\s*(?:hours?|hrs?)/)?.[1] || 0);
  const minutes = Number(text.match(/(\d+)\s*(?:minutes?|mins?)/)?.[1] || 0);

  return hours * 60 + minutes;
};

// ==================== Main Component ====================
export default function BookingForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  const { t } = useTranslation();

  // ==================== Extract State Data ====================
  const {
    formData,
    calculatedData,
    selectedVehicleInfo,
    allVehicles,
    allVehiclesPrices,
    summary,
  } = state || {};

  // ==================== State Management ====================
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);

  // Validation States
  const [validationErrors, setValidationErrors] = useState({});
  const [isStepValid, setIsStepValid] = useState(false);
  const [showStepErrors, setShowStepErrors] = useState(false);

  // Form Data States
  const [shipments, setShipments] = useState([createDefaultShipment()]);
  const [pickupForms, setPickupForms] = useState([createDefaultPickup()]);
  const [deliveryForms, setDeliveryForms] = useState([createDefaultDelivery()]);

  // Extra Services & Message
  const [extraServices, setExtraServices] = useState({
    loading: false,
    unloading: false,
  });
  const [message, setMessage] = useState("");

  // Invoice States
  const [selectedOption, setSelectedOption] = useState("Filling");
  const [invoiceEmail, setInvoiceEmail] = useState("");
  const [invoiceCompanyName, setInvoiceCompanyName] = useState("");
  const [invoiceSalutation, setInvoiceSalutation] = useState("");
  const [invoiceFirstName, setInvoiceFirstName] = useState("");
  const [invoiceLastName, setInvoiceLastName] = useState("");
  const [invoiceStreet, setInvoiceStreet] = useState("");
  const [invoiceAddressExtra, setInvoiceAddressExtra] = useState("");
  const [invoicePostalCity, setInvoicePostalCity] = useState("");
  const [invoicePhone, setInvoicePhone] = useState("");
  const [invoiceVatId, setInvoiceVatId] = useState("");

  // Payment States
  const [paymentMethod, setPaymentMethod] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponFeedback, setCouponFeedback] = useState(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  // Price States
  const [basePrice, setBasePrice] = useState({
    subTotal: "0.00",
    total: "0.00",
  });
  const [currentPrice, setCurrentPrice] = useState({
    subTotal: "0.00",
    total: "0.00",
  });
  const [routeDurationText, setRouteDurationText] = useState("");

  // ==================== Steps Configuration ====================
  const steps = useMemo(
    () => [
      { title: t("shipment"), id: 1 },
      { title: t("pickup_address"), id: 2 },
      { title: t("delivery_address"), id: 3 },
      { title: t("additional_services"), id: 4 },
      { title: t("overview"), id: 5 },
    ],
    [t],
  );

  // ==================== Effects ====================

  // Check Login Status
  useEffect(() => {
    const token =
      localStorage.getItem("token") || localStorage.getItem("userToken");
    setIsLoggedIn(!!token);
    setCurrentUser(getStoredUser());
  }, []);

  // Initialize Addresses from Previous Page
  useEffect(() => {
    if (!formData) return;

    const pickupAddresses = formData?.abholung || [];
    const deliveryAddresses = formData?.zustellung || [];

    // Initialize pickup forms
    const initialPickupForms =
      pickupAddresses.length > 0
        ? pickupAddresses.map((addr) => ({
            ...createDefaultPickup(),
            postalCity: addr?.address || addr,
            postalCity_lat: addr?.lat || "",
            postalCity_lng: addr?.lng || "",
          }))
        : [createDefaultPickup()];

    // Initialize delivery forms
    const initialDeliveryForms =
      deliveryAddresses.length > 0
        ? deliveryAddresses.map((addr) => ({
            ...createDefaultDelivery(),
            postalCity: addr?.address || addr,
            postalCity_lat: addr?.lat || "",
            postalCity_lng: addr?.lng || "",
          }))
        : [createDefaultDelivery()];

    setPickupForms(initialPickupForms);
    setDeliveryForms(initialDeliveryForms);

    // Create shipments based on pickup count
    const initialShipments = initialPickupForms.map(() =>
      createDefaultShipment(),
    );
    setShipments(initialShipments);
  }, [formData]);

  // Handle Repeat Booking
  useEffect(() => {
    if (state?.isRepeatBooking && state?.previousBooking) {
      fillFormWithPreviousBooking(state.previousBooking);
    }
  }, [state]);

  // Calculate Price
  const calculatePriceRef = useRef(null);

  useEffect(() => {
    if (state?.previousPrice && currentStep === 1) {
      setBasePrice({
        subTotal: state.previousPrice.sub_total || "0.00",
        total: state.previousPrice.total || "0.00",
      });
      return;
    }

    if (calculatePriceRef.current) {
      clearTimeout(calculatePriceRef.current);
    }

    calculatePriceRef.current = setTimeout(() => {
      calculatePriceFromAPI();
    }, 1000);

    return () => {
      if (calculatePriceRef.current) {
        clearTimeout(calculatePriceRef.current);
      }
    };
  }, [
    selectedVehicleInfo?.id,

    JSON.stringify(pickupForms.map((form) => form.postalCity).filter(Boolean)),
    JSON.stringify(
      deliveryForms.map((form) => form.postalCity).filter(Boolean),
    ),
    JSON.stringify(extraServices),
    JSON.stringify(
      shipments.map((s) => ({
        quantity: s.quantity,
        weight: s.weight,
      })),
    ),
  ]);

  useEffect(() => {
    setCurrentPrice(applyCouponToPrice(basePrice, appliedCoupon));
  }, [basePrice, appliedCoupon]);

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // Auto-validate on data change
  useEffect(() => {
    validateCurrentStep();
  }, [
    currentStep,
    shipments,
    pickupForms,
    deliveryForms,
    selectedOption,
    invoiceEmail,
    invoiceSalutation,
    invoiceFirstName,
    invoiceLastName,
    invoiceStreet,
    invoicePostalCity,
    invoicePhone,
    // invoiceVatId,
  ]);

  // ==================== Computed Values ====================

  const totalWeight = useMemo(() => {
    return shipments.reduce(
      (sum, s) => sum + Number(s?.quantity || 0) * Number(s?.weight || 0),
      0,
    );
  }, [shipments]);

  // ==================== Handlers ====================

  const handleExtraServiceChange = useCallback((service, isChecked) => {
    setExtraServices((prev) => ({
      ...prev,
      [service]: isChecked,
    }));
  }, []);

  const handleInvoiceOptionChange = useCallback(
    (option) => {
      setSelectedOption(option);

      if (option === "Wie Abholung" && pickupForms.length > 0) {
        const firstPickup = pickupForms[0];
        setInvoiceFirstName(firstPickup.firstName || "");
        setInvoiceLastName(firstPickup.lastName || "");
        setInvoiceCompanyName(firstPickup.companyName || "");
        setInvoiceStreet(firstPickup.street || "");
        setInvoicePostalCity(firstPickup.postalCity || "");
        setInvoicePhone(firstPickup.phone || "");
      } else if (option === "Wie Zustellung" && deliveryForms.length > 0) {
        const firstDelivery = deliveryForms[0];
        setInvoiceFirstName(firstDelivery.firstName || "");
        setInvoiceLastName(firstDelivery.lastName || "");
        setInvoiceCompanyName(firstDelivery.companyName || "");
        setInvoiceStreet(firstDelivery.street || "");
        setInvoicePostalCity(firstDelivery.postalCity || "");
        setInvoicePhone(firstDelivery.phone || "");
      } else if (option === "Filling") {
        setInvoiceFirstName("");
        setInvoiceLastName("");
        setInvoiceCompanyName("");
        setInvoiceSalutation("");
        setInvoiceStreet("");
        setInvoiceAddressExtra("");
        setInvoicePostalCity("");
        setInvoicePhone("");
        setInvoiceEmail("");
      }
    },
    [pickupForms, deliveryForms],
  );

  // ==================== Fill Form with Previous Booking ====================

  const fillFormWithPreviousBooking = useCallback((bookingData) => {
    console.log("📄 Filling form with previous booking data:", bookingData);

    // Fill pickup addresses
    if (
      bookingData.loading_postal &&
      Array.isArray(bookingData.loading_postal)
    ) {
      const initialPickupForms = bookingData.loading_postal.map(
        (postal, index) => ({
          ...createDefaultPickup(),
          postalCity: postal || "",
          companyName: bookingData.loading_company?.[index] || "",
          firstName: bookingData.loading_first_name?.[index] || "",
          lastName: bookingData.loading_last_name?.[index] || "",
          street: bookingData.loading_streat_no?.[index] || "",
          addressExtra: bookingData.loading_address_additional?.[index] || "",
          phone: bookingData.loading_phone?.[index] || "",
          pickupReference: bookingData.loading_reffernce?.[index] || "",
        }),
      );

      setPickupForms(initialPickupForms);

      // Create shipments based on pickup count
      if (
        bookingData.shipment_data &&
        Array.isArray(bookingData.shipment_data)
      ) {
        const convertedShipments = bookingData.shipment_data.map(
          (shipment) => ({
            quantity: shipment.quantity || 1,
            type: convertShipmentType(shipment.shipment_data) || "Palette",
            weight: shipment.weight || 100,
            length: shipment.length || 120,
            width: shipment.width || 80,
            height: shipment.height || 100,
            notes: shipment.message || "",
          }),
        );
        // Match shipments count with pickup count
        const shipmentsToUse = [];
        for (let i = 0; i < initialPickupForms.length; i++) {
          shipmentsToUse.push(convertedShipments[i] || createDefaultShipment());
        }
        setShipments(shipmentsToUse);
      } else {
        // Create default shipments
        const defaultShipments = initialPickupForms.map(() =>
          createDefaultShipment(),
        );
        setShipments(defaultShipments);
      }
    }

    // Fill delivery addresses
    if (
      bookingData.unloading_postal &&
      Array.isArray(bookingData.unloading_postal)
    ) {
      const initialDeliveryForms = bookingData.unloading_postal.map(
        (postal, index) => ({
          ...createDefaultDelivery(),
          postalCity: postal || "",
          companyName: bookingData.unloading_company?.[index] || "",
          firstName: bookingData.unloading_first_name?.[index] || "",
          lastName: bookingData.unloading_last_name?.[index] || "",
          street: bookingData.unloading_streat_no?.[index] || "",
          addressExtra: bookingData.unloading_address_additional?.[index] || "",
          phone: bookingData.unloading_phone?.[index] || "",
          deliveryReference: bookingData.unloading_reffernce?.[index] || "",
        }),
      );

      setDeliveryForms(initialDeliveryForms);
    }

    // Fill extra services
    if (bookingData.extra_service) {
      const services = {
        loading: Array.isArray(bookingData.extra_service)
          ? bookingData.extra_service.includes("loading")
          : bookingData.extra_service === "loading",
        unloading: Array.isArray(bookingData.extra_service)
          ? bookingData.extra_service.includes("unloading")
          : bookingData.extra_service === "unloading",
      };
      setExtraServices(services);
    }

    // Fill message
    if (bookingData.message) {
      setMessage(bookingData.message);
    }

    // Fill invoice data
    if (bookingData.invoice_email) {
      setInvoiceEmail(bookingData.invoice_email);
      setInvoiceCompanyName(bookingData.invoice_company || "");
      setInvoiceSalutation(bookingData.invoice_title || "");
      setInvoiceFirstName(bookingData.invoice_first_name || "");
      setInvoiceLastName(bookingData.invoice_last_name || "");
      setInvoiceStreet(bookingData.invoice_street_no || "");
      setInvoiceAddressExtra(bookingData.invoice_addition_address || "");
      setInvoicePostalCity(bookingData.invoice_postal_code || "");
      setInvoicePhone(bookingData.invoice_phone_number || "");
      setInvoiceVatId(bookingData.invoice_reffernce || "");

      if (bookingData.invoice_email && bookingData.invoice_first_name) {
        setSelectedOption("Filling");
      }
    }

    console.log("✅ Form filled successfully");
  }, []);

  // ==================== Price Calculation ====================
  const calculatePriceFromAPI = useCallback(async () => {
    if (!selectedVehicleInfo?.id) {
      calculatePriceLocally();
      return;
    }

    try {
      setIsCalculatingPrice(true);

      const calculationData = {
        vehicle_id: selectedVehicleInfo.id,
        // ✅ تأكد من استخدام القيم الفعلية فقط
        booking_from: pickupForms
          .map((form) => form.postalCity)
          .filter(Boolean),
        booking_to: deliveryForms
          .map((form) => form.postalCity)
          .filter(Boolean),

        crane: selectedVehicleInfo.crane,
      };

      // ✅ تحقق من وجود بيانات كافية
      if (
        calculationData.booking_from.length === 0 ||
        calculationData.booking_to.length === 0
      ) {
        calculatePriceLocally();
        return;
      }

      const response =
        await userAPI.bookings.calculateVehicles(calculationData);

      if (response.success) {
        const calculatedPrice = response.data.data.selected_vehicle;

        setRouteDurationText(
          response.data.data.duration1_text ||
            response.data.data.duration_text ||
            "",
        );

        let extraCost = 0;
        if (extraServices.loading) extraCost += EXTRA_SERVICE_PRICE;
        if (extraServices.unloading) extraCost += EXTRA_SERVICE_PRICE;

        const baseSubTotal = parseAmountValue(
          calculatedPrice.sub_total || calculatedPrice.total || 0,
        );
        const finalSubTotal = baseSubTotal + extraCost;
        const finalTotal = finalSubTotal * (1 + VAT_RATE);

        setBasePrice({
          subTotal: finalSubTotal.toFixed(2).replace(".", ","),
          total: finalTotal.toFixed(2).replace(".", ","),
        });
      } else {
        setRouteDurationText("");
        calculatePriceLocally();
      }
    } catch (error) {
      console.error("❌ Error calculating price:", error);
      setRouteDurationText("");
      calculatePriceLocally();
    } finally {
      setIsCalculatingPrice(false);
    }
  }, [selectedVehicleInfo, pickupForms, deliveryForms, extraServices]);

  // 🔴 دالة يدوية لإعادة حساب السعر عند الحاجة
  const manualRecalculatePrice = useCallback(() => {
    if (calculatePriceRef.current) {
      clearTimeout(calculatePriceRef.current);
    }
    calculatePriceFromAPI();
  }, [calculatePriceFromAPI]);

  const calculatePriceLocally = useCallback(() => {
    const basePrice =
      summary?.subTotal ||
      selectedVehicleInfo?.calculatedPrice?.subTotal ||
      totalWeight * BASE_PRICE_PER_KG;

    let extraCost = 0;
    if (extraServices.loading) extraCost += EXTRA_SERVICE_PRICE;
    if (extraServices.unloading) extraCost += EXTRA_SERVICE_PRICE;

    const calculatedSubTotal = parseAmountValue(basePrice) + extraCost;
    const calculatedTotal = calculatedSubTotal * (1 + VAT_RATE);

    setBasePrice({
      subTotal: calculatedSubTotal.toFixed(2).replace(".", ","),
      total: calculatedTotal.toFixed(2).replace(".", ","),
    });
  }, [summary, selectedVehicleInfo, totalWeight, extraServices]);

  const handleApplyCoupon = useCallback(async () => {
    const translateCouponMessage = (message) => {
      if (message === "Coupon already used by this customer") {
        return t("coupon.already_used");
      }

      if (
        message === "Coupon expired or fully used"
      ) {
        return t("coupon.expired_or_used");
      }

      return message || t("coupon.validation_failed");
    };

    if (!couponCode.trim()) {
      setCouponFeedback({
        type: "danger",
        message: t("coupon.enter_code"),
      });
      return;
    }

    setIsApplyingCoupon(true);
    setCouponFeedback(null);

    const resolvedEmail =
      currentUser?.email || invoiceEmail.trim() || guestEmail.trim();

    if (!isLoggedIn && !resolvedEmail) {
      setCouponFeedback({
        type: "danger",
        message: t("please_enter_email"),
      });
      setIsApplyingCoupon(false);
      return;
    }

    const couponPayload = {
      code: couponCode.trim().toUpperCase(),
      order_price: parseAmountValue(basePrice.subTotal),
    };

    if (currentUser?.id) {
      couponPayload.user_id = currentUser.id;
    }

    if (resolvedEmail) {
      couponPayload.user_email = resolvedEmail;
    }

    const response = await userAPI.coupons.validate({
      ...couponPayload,
    });

    if (!response.success) {
      setAppliedCoupon(null);
      setCouponFeedback({
        type: "danger",
        message: translateCouponMessage(response.message),
      });
      setIsApplyingCoupon(false);
      return;
    }

    const couponData = response.data?.data || {};

    if (!couponData.valid) {
      setAppliedCoupon(null);
      setCouponFeedback({
        type: "danger",
        message: translateCouponMessage(couponData.message),
      });
      setIsApplyingCoupon(false);
      return;
    }

    setAppliedCoupon({
      code: couponCode.trim().toUpperCase(),
      discountPercentage: Number(couponData.discount_percentage || 0),
    });
    setCouponCode(couponCode.trim().toUpperCase());
    setCouponFeedback({
      type: "success",
      message: t("coupon.applied_success", {
        discount: couponData.discount_percentage,
      }),
    });
    setIsApplyingCoupon(false);
  }, [couponCode, basePrice, currentUser, guestEmail, invoiceEmail, isLoggedIn, t]);

  const handleRemoveCoupon = useCallback(() => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponFeedback(null);
  }, []);

  // ==================== Validation ====================

  const validateCurrentStep = useCallback(() => {
  const errors = {};

  switch (currentStep) {
      case 1: {
        if (!Array.isArray(shipments) || shipments.length === 0) {
          errors.shipments = t("at_least_one_shipment_required");
        } else {
          shipments.forEach((shipment, index) => {
            if (!shipment.quantity || shipment.quantity < 1) {
              errors[`quantity_${index}`] = t("quantity_required");
            }
            if (!shipment.type) {
              errors[`type_${index}`] = t("shipment_type_required");
            }

            // ✅ التحقق من Sonstige - يجب إدخال وصف
            if (shipment.type === "Sonstige" && !shipment.customShipmentText?.trim()) {
              errors[`customShipmentText_${index}`] = t("please_enter_shipment_description");
            }

            // ✅ التحقق من الوزن والأبعاد للأنواع الأخرى
            if (shipment.type !== "Dokument" && shipment.type !== "Sonstige") {
              if (!shipment.weight || shipment.weight <= 0) {
                errors[`weight_${index}`] = t("weight_required");
              }
              if (!shipment.length || shipment.length <= 0) {
                errors[`length_${index}`] = t("length_required");
              }
              if (!shipment.width || shipment.width <= 0) {
                errors[`width_${index}`] = t("width_required");
              }
              if (!shipment.height || shipment.height <= 0) {
                errors[`height_${index}`] = t("height_required");
              }
            }

            // ✅ التحقق من عدد البالتات
            if (selectedVehicleInfo?.amount) {
              const totalPalletQuantity = shipments
                .filter(s => s.type === "Palette")
                .reduce((sum, s) => sum + (Number(s.quantity) || 0), 0);

              if (totalPalletQuantity > Number(selectedVehicleInfo.amount)) {
                errors.palletQuantity = t("pallet_quantity_exceeds_vehicle_limit", {
                  totalPalletQuantity,
                  maxPallets: selectedVehicleInfo.amount
                });
              }
            }
          });
        }
        break;
      }

      case 2: {
        pickupForms.forEach((form, index) => {
          if (!form.firstName?.trim())
            errors[`pickup_firstName_${index}`] = t("first_name_required");
          if (!form.lastName?.trim())
            errors[`pickup_lastName_${index}`] = t("last_name_required");
          if (!form.street?.trim())
            errors[`pickup_street_${index}`] = t("street_required");
          if (!form.postalCity?.trim())
            errors[`pickup_postalCity_${index}`] = t("postal_city_required");
          if (!form.pickupDate)
            errors[`pickup_date_${index}`] = t("pickup_date_required");
          if (!form.timeFrom)
            errors[`pickup_timeFrom_${index}`] = t("start_time_required");
          if (!form.timeTo)
            errors[`pickup_timeTo_${index}`] = t("end_time_required");
        });

        const dateErrors = validateAllDates(pickupForms, deliveryForms);
        dateErrors.forEach(({ field, message }) => {
          errors[field] = t(message);
        });
        break;
      }

      case 3: {
        deliveryForms.forEach((form, index) => {
          if (!form.firstName?.trim())
            errors[`delivery_firstName_${index}`] = t("first_name_required");
          if (!form.lastName?.trim())
            errors[`delivery_lastName_${index}`] = t("last_name_required");
          if (!form.street?.trim())
            errors[`delivery_street_${index}`] = t("street_required");
          if (!form.postalCity?.trim())
            errors[`delivery_postalCity_${index}`] = t("postal_city_required");
          if (!form.deliveryDate)
            errors[`delivery_date_${index}`] = t("delivery_date_required");
          if (!form.timeFrom)
            errors[`delivery_timeFrom_${index}`] = t("start_time_required");
          if (!form.timeTo)
            errors[`delivery_timeTo_${index}`] = t("end_time_required");
        });

        const dateErrors = validateAllDates(pickupForms, deliveryForms);
        dateErrors.forEach(({ field, message }) => {
          errors[field] = t(message);
        });
        break;
      }

      case 4:
        // Email is always required regardless of selected option
        if (!invoiceEmail?.trim()) {
          errors.invoiceEmail = t("invoice_email_required");
        } else if (!/\S+@\S+\.\S+/.test(invoiceEmail)) {
          errors.invoiceEmail = t("invalid_email_format");
        }

        if (selectedOption && selectedOption !== "Filling") {
          if (!invoiceSalutation?.trim())
            errors.invoiceSalutation = t("salutation_required");
          if (!invoiceFirstName?.trim())
            errors.invoiceFirstName = t("first_name_required");
          if (!invoiceLastName?.trim())
            errors.invoiceLastName = t("last_name_required");
          if (!invoiceStreet?.trim())
            errors.invoiceStreet = t("street_required");
          if (!invoicePostalCity?.trim())
            errors.invoicePostalCity = t("postal_city_required");
        }
        break;

      default:
        break;
    }

    setValidationErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    setIsStepValid(isValid);
    return isValid;
  }, [
    currentStep,
    shipments,
    pickupForms,
    deliveryForms,
    selectedOption,
    invoiceEmail,
    invoiceSalutation,
    invoiceFirstName,
    invoiceLastName,
    invoiceStreet,
    invoicePostalCity,
    invoicePhone,
    invoiceVatId,
    routeDurationText,
    t,
  ]);

  // ==================== Helper Functions ====================

  // ✅ التحقق من أن التاريخ ليس يوم الأحد
  const isSunday = (dateString) => {
    const date = new Date(dateString);
    return date.getDay() === 0; // 0 = Sunday
  };

  // ✅ التحقق من أن الوقت ضمن أوقات العمل (7:00 - 18:00)
  const isWithinWorkingHours = (timeString) => {
    if (!timeString) return false;
    const [hours, minutes] = timeString.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes;
    const startMinutes = 7 * 60; // 7:00
    const endMinutes = 18 * 60; // 18:00
    return totalMinutes >= startMinutes && totalMinutes <= endMinutes;
  };

  // ✅ التحقق من أن وقت التنزيل بعد وقت التحميل (نفس اليوم)
  const isValidTimeSequence = (pickupDate, pickupTime, deliveryDate, deliveryTime) => {
    const pickup = new Date(`${pickupDate}T${pickupTime}:00`);
    const delivery = new Date(`${deliveryDate}T${deliveryTime}:00`);
    
    // التنزيل يجب أن يكون بعد التحميل
    if (delivery < pickup) {
      return {
        valid: false,
        message: 'delivery_time_before_pickup'
      };
    }
    
    // إذا كان نفس اليوم، يجب أن يكون الفارق 30 دقيقة على الأقل
    if (pickupDate === deliveryDate) {
      const diffMinutes = (delivery - pickup) / (1000 * 60);
      if (diffMinutes < 30) {
        return {
          valid: false,
          message: 'delivery_time_too_close'
        };
      }
    }
    
    return { valid: true };
  };

  // ✅ التحقق من جميع التواريخ في Step 2 و Step 3
  const validateAllDates = (pickupForms, deliveryForms) => {
    const errors = [];
    
    // 1. التحقق من أيام الأحد وأوقات العمل لكل pickup
    pickupForms.forEach((form, index) => {
      if (form.pickupDate) {
        if (isSunday(form.pickupDate)) {
          errors.push({
            field: `pickup_date_${index}`,
            message: 'pickup_sunday_not_allowed'
          });
        }
      }
      if (form.timeFrom && !isWithinWorkingHours(form.timeFrom)) {
        errors.push({
          field: `pickup_timeFrom_${index}`,
          message: 'pickup_time_outside_working_hours'
        });
      }
      if (form.timeTo && !isWithinWorkingHours(form.timeTo)) {
        errors.push({
          field: `pickup_timeTo_${index}`,
          message: 'pickup_time_outside_working_hours'
        });
      }

      // في حجز نفس اليوم فقط: أول مكان تحميل يحتاج نافذة ساعتين على الأقل.
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
        now.getDate(),
      ).padStart(2, "0")}`;
      if (
        index === 0 &&
        form.pickupDate === today &&
        form.timeFrom &&
        form.timeTo
      ) {
        const [fromHour, fromMinute] = form.timeFrom.split(":").map(Number);
        const [toHour, toMinute] = form.timeTo.split(":").map(Number);
        const difference = toHour * 60 + toMinute - (fromHour * 60 + fromMinute);

        if (difference < 120) {
          errors.push({
            field: "pickup_timeTo_0",
            message: "pickup_time_window_two_hours",
          });
        }
      }

      if (
        index === 0 &&
        form.pickupDate &&
        form.timeTo &&
        deliveryForms[0]?.deliveryDate === form.pickupDate &&
        deliveryForms[0]?.timeFrom
      ) {
        const pickupEnd = new Date(`${form.pickupDate}T${form.timeTo}:00`);
        const deliveryStart = new Date(
          `${deliveryForms[0].deliveryDate}T${deliveryForms[0].timeFrom}:00`,
        );
        const requiredMinutes = parseDurationMinutes(routeDurationText);
        const difference = (deliveryStart - pickupEnd) / (1000 * 60);

        if (difference < requiredMinutes) {
          errors.push({
            field: "delivery_timeFrom_0",
            message: "delivery_time_before_route_duration",
          });
        }
      }
    });
    
    // 2. التحقق من أيام الأحد وأوقات العمل لكل delivery
    deliveryForms.forEach((form, index) => {
      if (form.deliveryDate) {
        if (isSunday(form.deliveryDate)) {
          errors.push({
            field: `delivery_date_${index}`,
            message: 'delivery_sunday_not_allowed'
          });
        }
      }
      // if (form.timeFrom && !isWithinWorkingHours(form.timeFrom)) {
      //   errors.push({
      //     field: `delivery_timeFrom_${index}`,
      //     message: 'delivery_time_outside_working_hours'
      //   });
      // }
      // if (form.timeTo && !isWithinWorkingHours(form.timeTo)) {
      //   errors.push({
      //     field: `delivery_timeTo_${index}`,
      //     message: 'delivery_time_outside_working_hours'
      //   });
      // }
    });
    
    // 3. التحقق من تسلسل التواريخ (pickup -> delivery)
    const maxPairs = Math.min(pickupForms.length, deliveryForms.length);
    for (let i = 0; i < maxPairs; i++) {
      const pickup = pickupForms[i];
      const delivery = deliveryForms[i];
      
      if (pickup.pickupDate && pickup.timeFrom && delivery.deliveryDate && delivery.timeFrom) {
        const sequenceCheck = isValidTimeSequence(
          pickup.pickupDate,
          pickup.timeFrom,
          delivery.deliveryDate,
          delivery.timeFrom
        );
        
        if (!sequenceCheck.valid) {
          errors.push({
            field: `delivery_timeFrom_${i}`,
            message: sequenceCheck.message
          });
        }
      }
    }
    
    return errors;
  };

  // ==================== Navigation Handlers ====================

  const handleNextStep = () => {
    if (validateCurrentStep()) {
      setShowStepErrors(false);
      setCurrentStep((prev) => prev + 1);
    } else {
      setShowStepErrors(true);
      setTimeout(() => {
        const firstErrorKey = Object.keys(validationErrors)[0];
        const errorElement = document.querySelector(
          `[data-error="${firstErrorKey}"]`,
        );
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElement.focus();
        }
      }, 100);
    }
  };

  const handlePreviousStep = () => {
    setShowStepErrors(false);
    setCurrentStep((prev) => prev - 1);
  };

  // ==================== Submit Handler ====================

  const handleSubmit = async () => {
    // Validate payment method
    if (!paymentMethod) {
      Swal.fire({
        icon: "warning",
        title: t("payment_method_required"),
        text: t("please_select_payment_method"),
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    // Validate guest email
    if (!isLoggedIn && !guestEmail.trim()) {
      Swal.fire({
        icon: "warning",
        title: t("email_required"),
        text: t("please_enter_email"),
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    if (!isLoggedIn && guestEmail.trim() && !/\S+@\S+\.\S+/.test(guestEmail)) {
      Swal.fire({
        icon: "warning",
        title: t("invalid_email"),
        text: t("please_enter_valid_email"),
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    if (totalWeight > selectedVehicleInfo?.weight) {
      Swal.fire({
        icon: "error",
        title: t("overload_error"),
        text: t("weight_exceeds_vehicle_limit", {
          totalWeight,
          maxWeight: selectedVehicleInfo?.weight,
        }),
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    // Prepare shipment data
    const shipmentDataArray = shipments.map((s) => {
      let shipmentValue = SHIPMENT_TYPE_MAP[s.type] || "other";

      const shipmentData = {
        quantity: s.quantity,
        // shipment_data: shipmentValue,
        weight: s.weight,
        length: s.length,
        width: s.width,
        height: s.height,
        message: s.notes,
      };
      
      // if (s.type === "Sonstige" && s.customShipmentText?.trim()) {
      //   shipmentValue = s.customShipmentText.trim();
      // }

      if (s.type === "Sonstige" && s.customShipmentText?.trim()) {
        // ✅ نرسل النص المخصص مباشرة في shipment_data
        shipmentData.shipment_data = s.customShipmentText.trim();
        // ✅ نضيف حقل إضافي لتحديد أن هذا من نوع other
        shipmentData.shipment_type = "other";
      } else {
        // ✅ للأنواع الأخرى نرسل القيمة المعيارية
        shipmentData.shipment_data = SHIPMENT_TYPE_MAP[s.type] || "other";
        shipmentData.shipment_type = SHIPMENT_TYPE_MAP[s.type] || "other";
      }
      
      // return {
      //   quantity: s.quantity,
      //   shipment_data: shipmentValue,
      //   weight: s.weight,
      //   length: s.length,
      //   width: s.width,
      //   height: s.height,
      //   message: s.notes,
      // };

      return shipmentData;
    });

    // Prepare extra services
    const extraServiceArray = [];
    if (extraServices.loading) extraServiceArray.push("loading");
    if (extraServices.unloading) extraServiceArray.push("unloading");

    // Prepare booking data
    const data = {
      vehicle_id: selectedVehicleInfo?.id || calculatedData?.vehicle_id || 4,
      crane: selectedVehicleInfo?.crane || "no",
      shipment_data: shipmentDataArray,
      extra_service: extraServiceArray,
      message: message,

      // Pickup data
      booking_from: pickupForms.map((f) => f.postalCity),
      loading_company: pickupForms.map(
        (f) => f.companyName || `${f.firstName} ${f.lastName}`,
      ),
      loading_first_name: pickupForms.map((f) => f.firstName),
      loading_last_name: pickupForms.map((f) => f.lastName),
      loading_streat_no: pickupForms.map((f) => f.street),
      loading_address_additional: pickupForms.map((f) => f.addressExtra),
      loading_postal: pickupForms.map((f) => f.postalCity),
      loading_phone: pickupForms.map((f) => f.phone),
      loading_reffernce: pickupForms.map((f) => f.pickupReference),
      loading_date_time: pickupForms.map((f) => {
        const date = f.pickupDate || new Date().toISOString().split("T")[0];
        const time = f.timeFrom || "09:00";
        return `${date}T${time}:00`;
      }),

      // Delivery data
      booking_to: deliveryForms.map((f) => f.postalCity),
      unloading_company: deliveryForms.map(
        (f) => f.companyName || `${f.firstName} ${f.lastName}`,
      ),
      unloading_first_name: deliveryForms.map((f) => f.firstName),
      unloading_last_name: deliveryForms.map((f) => f.lastName),
      unloading_streat_no: deliveryForms.map((f) => f.street),
      unloading_address_additional: deliveryForms.map((f) => f.addressExtra),
      unloading_postal: deliveryForms.map((f) => f.postalCity),
      unloading_phone: deliveryForms.map((f) => f.phone),
      unloading_reffernce: deliveryForms.map((f) => f.deliveryReference),
      unloading_date_time: deliveryForms.map((f) => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const date = f.deliveryDate || tomorrow.toISOString().split("T")[0];
        const time = f.timeFrom || "09:00";
        return `${date}T${time}:00`;
      }),

      // Invoice data
      invoice_email: invoiceEmail || guestEmail,
      invoice_company: invoiceCompanyName,
      invoice_title: invoiceSalutation,
      invoice_first_name: invoiceFirstName,
      invoice_last_name: invoiceLastName,
      invoice_street_no: invoiceStreet,
      invoice_addition_address: invoiceAddressExtra,
      invoice_postal_code: invoicePostalCity,
      invoice_phone_number: invoicePhone,
      invoice_reffernce: invoiceVatId,

      // Payment data
      user_email: isLoggedIn ? invoiceEmail || guestEmail : guestEmail,
      payment_method: paymentMethod,
      sub_total: currentPrice.subTotal,
      total: currentPrice.total,
      coupon_code: appliedCoupon?.code || undefined,
    };

    setIsSubmitting(true);

    try {
      const response = await userAPI.bookings.store(data);
      const res = response.data || response;
      console.log("📦 API response:", res.data);

      if (res.success) {
        Swal.fire({
          icon: "success",
          title: t("booking_success"),
          text: t("booking_created_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });

        setTimeout(() => {
          const paymentMethod = res.payment_method || res.data?.payment_method;
          const paymentUrl = res.payment_url || res.data?.payment_url;

          if (
            (paymentMethod === "stripe" || paymentMethod === "paypal" || paymentMethod === "klarna") &&
            paymentUrl
          ) {
            window.location.href = paymentUrl;
          } else {
            navigate("/thank-you");
          }
        }, 2000);
      } else {
        Swal.fire({
          icon: "error",
          title: t("booking_error"),
          text: res.message || t("unknown_error"),
          confirmButtonColor: "#3085d6",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: error?.message || t("unknown_error"),
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==================== Render Form Steps ====================

  const renderForm = () => {
    const errorAlert = showStepErrors && !isStepValid && (
      <Alert variant="danger" className="mb-3">
        <FaExclamationCircle className="me-2" />
        {currentStep === 1
          ? t("fix_errors_before_continue")
          : t("fill_all_required_fields")}
      </Alert>
    );

    switch (currentStep) {
      case 1:
        return (
          <>
            {errorAlert}
            <StepOne
              shipments={shipments}
              setShipments={setShipments}
              validationErrors={showStepErrors ? validationErrors : {}}
              selectedVehicleInfo={selectedVehicleInfo}
              extraServices={extraServices}
              onExtraServiceChange={handleExtraServiceChange}
              message={message}
              setMessage={setMessage}
              pickupForms={pickupForms}
            />
          </>
        );

      case 2:
        return (
          <>
            {errorAlert}
            <StepTwoWrapper
              forms={pickupForms}
              setForms={setPickupForms}
              validationErrors={showStepErrors ? validationErrors : {}}
            />
          </>
        );

      case 3:
        return (
          <>
            {errorAlert}
            <StepThree
              forms={deliveryForms}
              setForms={setDeliveryForms}
              validationErrors={showStepErrors ? validationErrors : {}}
              pickupForms={pickupForms}
              durationText={routeDurationText}
            />
          </>
        );

      case 4:
        return (
          <>
            {errorAlert}
            <StepFour
              selectedOption={selectedOption}
              setSelectedOption={handleInvoiceOptionChange}
              email={invoiceEmail}
              setEmail={setInvoiceEmail}
              companyName={invoiceCompanyName}
              setCompanyName={setInvoiceCompanyName}
              salutation={invoiceSalutation}
              setSalutation={setInvoiceSalutation}
              firstName={invoiceFirstName}
              setFirstName={setInvoiceFirstName}
              lastName={invoiceLastName}
              setLastName={setInvoiceLastName}
              street={invoiceStreet}
              setStreet={setInvoiceStreet}
              addressExtra={invoiceAddressExtra}
              setAddressExtra={setInvoiceAddressExtra}
              postalCity={invoicePostalCity}
              setPostalCity={setInvoicePostalCity}
              phone={invoicePhone}
              setPhone={setInvoicePhone}
              vatId={invoiceVatId}
              setVatId={setInvoiceVatId}
              validationErrors={showStepErrors ? validationErrors : {}}
              pickupForms={pickupForms}
              deliveryForms={deliveryForms}
            />
          </>
        );

      case 5:
        return (
          <StepFive
            pickupForms={pickupForms}
            deliveryForms={deliveryForms}
            shipmentsByStop={shipments}
            totalWeight={totalWeight}
            subTotal={currentPrice.subTotal}
            price={currentPrice.total}
            selectedOption={selectedOption}
            invoiceEmail={invoiceEmail}
            invoiceCompanyName={invoiceCompanyName}
            invoiceSalutation={invoiceSalutation}
            invoiceFirstName={invoiceFirstName}
            invoiceLastName={invoiceLastName}
            invoiceStreet={invoiceStreet}
            invoicePostalCity={invoicePostalCity}
            invoicePhone={invoicePhone}
            invoiceAddressExtra={invoiceAddressExtra}
            invoiceVatId={invoiceVatId}
            extraServices={extraServices}
            message={message}
            setCurrentStep={setCurrentStep}
            previousData={{
              selectedVehicle: selectedVehicleInfo,
              calculatedData: calculatedData,
              summary: summary,
              allVehicles: allVehicles,
            }}
          />
        );

      default:
        return null;
    }
  };

  const hasWeightShipment = shipments.some(
    (shipment) => shipment.type !== "Dokument"
  );

  // ==================== Render Overview Section ====================

  const renderOverviewSection = (title, stepNumber, children) => (
    <div className="overview-section">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="muted mb-0">{title}</h6>
        <Button
          variant="warning"
          size="sm"
          onClick={() => setCurrentStep(stepNumber)}
        >
          <MdEditSquare />
        </Button>
      </div>
      <div className="overview-box">{children}</div>
    </div>
  );

  const renderAddressInfo = (form, index, type) => {
    const isPickup = type === "pickup";
    const label = isPickup ? t("pickup") : t("delivery");
    return (
      <div key={index} className="small pb-2 mb-2 border-bottom">
        <strong>
          {label} {index + 1}:
        </strong>
        {form.companyName && (
          <>
            <br />
            {form.companyName}
          </>
        )}
        <br />
        {form.firstName} {form.lastName}
        <br />
        {form.street}
        {form.addressExtra && (
          <>
            <br />
            {form.addressExtra}
          </>
        )}
        <br />
        {form.postalCity}
        <br />
        {form.phone}
        <div className="d-flex align-items-center gap-1 ">
          {t("clientDetails.pickup.reference")}
          <span>
            {(isPickup ? form.pickupReference : form.deliveryReference) && (
              <div className="blak" style={{ color: "black !important" }}>
                {isPickup ? form.pickupReference : form.deliveryReference}
                <br />
              </div>
            )}
          </span>
        </div>
        {(isPickup ? form.pickupDate : form.deliveryDate) && (
          <div>
            <br />
            <FaCalendarAlt className="me-1 text-muted" />{" "}
            {formatDisplayDate(isPickup ? form.pickupDate : form.deliveryDate)}
            <br />
            <FaClock className="me-1 text-muted" /> {form.timeFrom} -{" "}
            {form.timeTo}
          </div>
        )}
      </div>
    );
  };

  // ==================== Main Render ====================

  return (
    <>
      <NavbarMenu />
      <section className="shipmentt-section py-4">
        <Container>
          {/* Stepper */}
          <div className="stepper-wrapper mb-5 position-relative">
            <div className="progress-track">
              <div
                className="progress-fill"
                style={{
                  width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
                }}
              />
            </div>

            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`step-item ${
                  currentStep === step.id
                    ? "active"
                    : currentStep > step.id
                      ? "completed"
                      : ""
                } ${currentStep === step.id && !isStepValid ? "invalid" : ""}`}
                style={{ left: `${(index / (steps.length - 1)) * 100}%` }}
                onClick={() => {
                  if (currentStep > step.id) {
                    setCurrentStep(step.id);
                  }
                }}
              >
                <div className="step-circle">
                  {currentStep > step.id ? "✓" : step.id}
                  {currentStep === step.id && !isStepValid && (
                    <span className="step-error-indicator">!</span>
                  )}
                </div>
                <div className="step-title">{step.title}</div>
              </div>
            ))}
          </div>

          <Row>
            {/* Main Form */}
            <Col lg={8} md={12} sm={12}>
              <Card className="form-card shadow-sm border-0">
                <Card.Body>
                  <div className="form-header mb-3">
                    <h3>
                      {steps.find((s) => s.id === currentStep)?.title ||
                        steps[0].title}
                    </h3>
                  </div>

                  {renderForm()}

                  {/* Navigation Buttons */}
                  <div className="d-flex justify-content-start mt-3">
                    {currentStep > 1 && (
                      <Button
                        variant="secondary"
                        className="me-2"
                        onClick={handlePreviousStep}
                      >
                        {t("back")}
                      </Button>
                    )}
                    {currentStep < steps.length && (
                      <Button className="btn-orange" onClick={handleNextStep}>
                        {t("next")} <FaChevronRight className="ms-2" />
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Sidebar */}
            <Col lg={4} md={12} sm={12} className="mt-3 mt-lg-0">
              {currentStep !== 5 ? (
                <Card
                  className="overview-card shadow-sm border-0 sticky-lg-top"
                  style={{ top: "20px" }}
                >
                  <Card.Body>
                    <h5 className="overview-title mb-3">📋 {t("overview")}</h5>

                    {/* Pickup Addresses */}
                    {currentStep > 2 &&
                      renderOverviewSection(
                        t("pickup"),
                        2,
                        pickupForms.length > 0 ? (
                          pickupForms.map((form, index) =>
                            renderAddressInfo(form, index, "pickup"),
                          )
                        ) : (
                          <div className="small">
                            {t("no_pickup_addresses")}
                          </div>
                        ),
                      )}

                    {/* Delivery Addresses */}
                    {currentStep > 3 &&
                      renderOverviewSection(
                        t("delivery"),
                        3,
                        deliveryForms.length > 0 ? (
                          deliveryForms.map((form, index) =>
                            renderAddressInfo(form, index, "delivery"),
                          )
                        ) : (
                          <div className="small">
                            {t("no_delivery_addresses")}
                          </div>
                        ),
                      )}

                    {/* Invoice Address */}
                    {currentStep > 4 &&
                      renderOverviewSection(
                        t("invoice_address"),
                        4,
                        <div className="small">
                          {invoiceEmail && (
                            <div>
                              <strong>{t("email_label")}:</strong>{" "}
                              {invoiceEmail}
                            </div>
                          )}
                          {invoiceCompanyName && (
                            <div>
                              <strong>{t("company_label")}:</strong>{" "}
                              {invoiceCompanyName}
                            </div>
                          )}
                          {(invoiceFirstName || invoiceLastName) && (
                            <div>
                              <strong>{t("name_label")}:</strong>{" "}
                              {invoiceSalutation} {invoiceFirstName}{" "}
                              {invoiceLastName}
                            </div>
                          )}
                          {invoiceStreet && (
                            <div>
                              <strong>{t("address_label")}:</strong>{" "}
                              {invoiceStreet}
                            </div>
                          )}
                          {invoiceAddressExtra && (
                            <div>
                              <strong>{t("address_extra_label")}:</strong>{" "}
                              {invoiceAddressExtra}
                            </div>
                          )}
                          {invoicePostalCity && (
                            <div>
                              <strong>{t("city_label")}:</strong>{" "}
                              {invoicePostalCity}
                            </div>
                          )}
                          {invoicePhone && (
                            <div>
                              <strong>{t("phone_label")}:</strong>{" "}
                              {invoicePhone}
                            </div>
                          )}
                          {invoiceVatId && (
                            <div>
                              <strong>{t("vat_id_label")}:</strong>{" "}
                              {invoiceVatId}
                            </div>
                          )}
                        </div>,
                      )}

                    {/* Selected Vehicle */}
                    {selectedVehicleInfo && (
                      <div className="overview-section">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="muted mb-0">
                            {t("selected_vehicle")}
                          </h6>
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() => (window.location.href = "/bookings")}
                          >
                            <MdEditSquare />
                          </Button>
                        </div>
                        <div className="overview-box">
                          <div className="small">
                            <strong>{selectedVehicleInfo.name}</strong>
                            <br />
                            {selectedVehicleInfo.size ||
                              selectedVehicleInfo.vehicle_size}
                            <br />
                            {selectedVehicleInfo.id === 4 &&
                              selectedVehicleInfo.crane && (
                                <div>
                                  {selectedVehicleInfo.crane === "yes"
                                    ? t("with_crane")
                                    : t("without_crane")}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Route Info */}
                    {summary && (
                      <div className="overview-section">
                        <h6 className="muted">{t("route_info")}</h6>
                        <div className="overview-box">
                          <div className="small">
                            <strong>{t("distance")}:</strong> {summary.distance}
                            <br />
                            <strong>{t("duration")}:</strong> {summary.duration}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Shipments */}
                    {currentStep > 1 &&
                      renderOverviewSection(
                        t("shipments"),
                        1,
                        <>
                          {shipments.length > 0 ? (
                            shipments.map((shipment, index) => (
                              <div key={index} className="small mb-2">
                                <strong>
                                  {shipment.quantity}x {shipment.type}
                                </strong>

                                {shipment.type !== "Dokument" && (
                                  <>
                                    <br />
                                    {shipment.weight} kg, {shipment.length}×
                                    {shipment.width}×{shipment.height} cm
                                  </>
                                )}

                                {shipment.notes && (
                                  <div>
                                    <em>{shipment.notes}</em>
                                  </div>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="small">{t("no_shipments")}</div>
                          )}

                          {shipments.length > 0 && hasWeightShipment && (
                            <div className="mt-2 pt-2 border-top">
                              <strong>{t("total_weight")}:</strong> {totalWeight} kg
                            </div>
                          )}
                        </>
                      )}

                    {/* Extra Services */}
                    {currentStep > 1 &&
                      renderOverviewSection(
                        t("extra_services"),
                        1,
                        <div className="small">
                          {extraServices.loading && (
                            <div>
                              ✅ {t("loading_service")} (+{EXTRA_SERVICE_PRICE}
                              €)
                            </div>
                          )}
                          {extraServices.unloading && (
                            <div>
                              ✅ {t("unloading_service")} (+
                              {EXTRA_SERVICE_PRICE}
                              €)
                            </div>
                          )}
                          {!extraServices.loading &&
                            !extraServices.unloading &&
                            t("no_extra_services")}
                        </div>,
                      )}

                    {/* Message */}
                    {message && currentStep > 1 && (
                      <div className="overview-section">
                        <h6 className="muted">{t("message")}</h6>
                        <div className="overview-box">
                          <div className="small">{message}</div>
                        </div>
                      </div>
                    )}

                    {/* Price */}
                    <div className="overview-price mt-3 text-center text-light">
                      <div className="price-header">
                        <h2 className="price-title mb-1">{t("preis")}</h2>
                        {isCalculatingPrice ? (
                          <div className="price-amount">
                            <Spinner
                              animation="border"
                              size="sm"
                              className="me-2"
                            />
                            {t("calculating")}
                          </div>
                        ) : (
                          <div className="price-amount">
                            <strong style={{ color: "#0ba60b" }}>
                              {currentPrice.subTotal}€
                            </strong>{" "}
                            {t("netto")}
                          </div>
                        )}
                      </div>
                      <div className="price-breakdown">
                        <div className="price-row total">
                          {t("plus_vat", {
                            vatRate: (VAT_RATE * 100).toFixed(0),
                          })}{" "}
                          <strong style={{ color: "#0ba60b" }}>
                            {currentPrice.total}€
                          </strong>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ) : (
                /* Payment Card */
                <Card className="payment-card shadow-sm border-0 overview-card">
                  <Card.Body>
                    <h5 className="fw-bold">{t("reserve_now")}</h5>
                    <p className="mb-3">
                      {t("select_payment_method_to_complete")}
                    </p>

                    {/* Guest Email Input */}
                    {!isLoggedIn && (
                      <Form.Group className="mb-3">
                        <Form.Label>{t("please_enter_your_email")}:</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder={t("email_address")}
                          value={guestEmail}
                          onChange={(e) => setGuestEmail(e.target.value)}
                          required
                          isInvalid={
                            guestEmail && !/\S+@\S+\.\S+/.test(guestEmail)
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {t("please_enter_valid_email")}
                        </Form.Control.Feedback>
                        <small className="text-muted">
                          {t("email_required_for_payment_confirmation")}
                        </small>
                      </Form.Group>
                    )}

                    {/* Payment Methods */}
                    <Form className="mb-3">
                      <Form.Check
                        type="radio"
                        name="payment"
                        label={
                          <>
                            <FaPaypal size={28} className="me-2 text-primary" />{" "}
                            {t("paypal")}
                          </>
                        }
                        checked={paymentMethod === "paypal"}
                        onChange={() => setPaymentMethod("paypal")}
                        className="mb-2"
                      />

                      <Form.Check
                        type="radio"
                        name="payment"
                        label={
                          <>
                            <FaCcVisa size={28} className="me-2 text-primary" />
                            <FaCcMastercard
                              size={28}
                              className="me-2 text-danger"
                            />
                            <FaCcDiscover
                              size={28}
                              className="me-2 text-warning"
                            />
                            <FaCcAmex size={28} className="me-2 text-info" />
                            {t("credit_card_stripe")}
                          </>
                        }
                        checked={paymentMethod === "stripe"}
                        onChange={() => setPaymentMethod("stripe")}
                        className="mb-2"
                      />

                      <Form.Check
                        type="radio"
                        name="payment"
                        label={
                          <>
                            <SiKlarna size={28} className="me-2 klarna-icon" />{" "}
                            {t("klarna")}
                          </>
                        }
                        checked={paymentMethod === "klarna"}
                        onChange={() => setPaymentMethod("klarna")}
                        className="mb-2"
                      />

                      {isLoggedIn && (
                        <Form.Check
                          type="radio"
                          name="payment"
                          label={t("bank_transfer")}
                          checked={paymentMethod === "Überweisung"}
                          onChange={() => setPaymentMethod("Überweisung")}
                        />
                      )}

                      {paymentMethod === "Überweisung" && (
                        <div className="bank-transfer-notice mt-3 p-3 bg-light border rounded">
                          <p className="mb-1 small text-muted">
                            <strong>{t("bank_transfer_notice_title")}</strong>
                          </p>
                          <p className="mb-0 small text-muted">
                            {t("bank_transfer_notice")}
                          </p>
                        </div>
                      )}
                    </Form>

                    <div className="mt-3">
                      <Form.Label className="fw-semibold">
                        {t("coupon.label")}
                      </Form.Label>
                      <InputGroup className="mb-2">
                        <Form.Control
                          placeholder={t("coupon.placeholder")}
                          value={couponCode}
                          onChange={(event) =>
                            setCouponCode(event.target.value.toUpperCase())
                          }
                        />
                        <Button
                          variant="outline-primary"
                          onClick={handleApplyCoupon}
                          disabled={isApplyingCoupon}
                        >
                          {isApplyingCoupon
                            ? t("coupon.applying")
                            : t("coupon.apply")}
                        </Button>
                      </InputGroup>

                      {couponFeedback && (
                        <Alert variant={couponFeedback.type} className="py-2">
                          {/* <div>{t(couponFeedback.message)}</div> */}
                          <div>{t('coupon.validation_failed')}</div>
                          {appliedCoupon && (
                            <div className="mt-2">
                              <span
                                style={{
                                  textDecoration: "line-through",
                                  color: "#6c757d",
                                  marginRight: "8px",
                                }}
                              >
                                {basePrice.subTotal}€
                              </span>
                              <strong>{currentPrice.subTotal}€</strong>
                                <Button
                                  variant="link"
                                  className="p-0 ms-2 align-baseline"
                                  onClick={handleRemoveCoupon}
                                >
                                {t("coupon.remove")}
                              </Button>
                            </div>
                          )}
                        </Alert>
                      )}
                    </div>

                    {/* Submit Button */}
                    <Button
                      className="btn-orange w-100 mt-3"
                      onClick={handleSubmit}
                      disabled={
                        !paymentMethod ||
                        (!isLoggedIn && !guestEmail.trim()) ||
                        isSubmitting
                      }
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          {t("processing")}
                        </>
                      ) : (
                        t("complete_booking")
                      )}
                    </Button>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Container>
      </section>
      <Footer />
    </>
  );
}
